/**
 * SOLID Review
 * S — Good. One concern: how a tab gets into the game screen — the session's start, identity, the stored
 *     game, and what to do once both have settled.
 * O — Good. A new way in is one more decision and one more intent.
 * L — N/A — functions.
 * I — Good. One function per decision on the contract.
 * D — Strong. The session draft, FE1's round mapping, the schemas' types.
 */
import { decided, refuse, REFUSAL, type IEntityDecision, type TDecisionResult } from '../shared';
import { applyActiveRound } from '../table/catchUp';
import {
  draftOf, settleSession, withPlayer, guestPlayer, restoredRoom, inLobby, seated, type IConfigSettleSession,
} from './sessionDraft';
import type { IPropsClientChanges, IPropsClientInput } from '../../ports/schemas/ClientChanges';
import type { IEntityClientAccount, IEntityClientPlayer } from '../../ports/schemas/ClientSession';
import type { IEntityTableAck } from '../../ports/schemas/TableEvents';
import type {
  ICommandSessionStarted, ICommandPlanEntry, ICommandMeAnswered, ICommandResumeValidated, ICommandPlanRestore,
  ICommandRestoreJoined, ICommandRoundRestored, ICommandPlanIntent, IEntityEntryPlan, IEntitySignOut, IEntityProceed,
  IEntityIntent, IEntityRoundRead, IPayloadSeat,
} from '../../ports/schemas/SessionMoves';
import type { ICommandNothing } from '../../ports/schemas/TableMoves';

/**
 * GETTING INTO THE GAME (frontend strict-ports plan, FE2, 2026-10-03). These were four hooks running
 * side by side whenever the game screen mounted — identity bootstrap, session recovery, cold-login rejoin,
 * smart-join routing — plus MainMenuPanel's auto-host and its redirect, each watching the store for its
 * moment. They are one entry now, in this order: identity and the stored game (side by side, as before),
 * then, if no seat was taken, the code, the cold rejoin, the auto-host, or back to the welcome screen.
 *
 * Moved, not rewritten: every condition below is one of the hooks' guards. Where the order is new, it is
 * flagged — the stored game and a table code used to RACE, and the code was dropped when the stored game
 * won; the stored game now goes first, and the code runs only if it did not seat the player.
 */

type TSession<D> = TDecisionResult<IEntityDecision<IPropsClientChanges, D>>;

/** useIdentityBootstrap's message when a join was waiting on a token the server refused. */
const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please sign in again to join the table.';
/** The welcome screen's line when `me` could not be asked (FE3). */
const ME_UNREACHABLE = 'Could not reach the server. Please try again.';
const ROOM_FINISHED = 'FINISHED';
const ROOM_IN_PROGRESS = 'IN_PROGRESS';

const NOTHING: IPropsClientChanges = {};
const APPLIED: IEntityTableAck = { applied: true };
const NOT_APPLIED: IEntityTableAck = { applied: false };
const STAY: IEntityProceed = { proceed: false };
const GO_ON: IEntityProceed = { proceed: true };
const NO_READ: IEntityRoundRead = { read: null };

/**
 * The page loaded (authStore's `hydrate` and AuthRouter's URL bootstrap). The account is the one a stored,
 * still-valid token belongs to (FE3: `ClientAuthServer.account`, which read and checked it — hydrate's JWT
 * half), or the stored guest, or nobody; a token makes a stored guest forgotten, as hydrate did. Where the
 * page lands: Stripe's return, a `?join=` code (the game screen, with or without identity), a stored game
 * for a known player (the game screen, where it resumes), or the welcome screen. The "restoring" overlay is
 * up while a game is stored, as the game store's initial state had it.
 */
export function decideSessionStarted(input: IPropsClientInput, command: ICommandSessionStarted): TSession<IEntityTableAck> {
  const { session } = input;
  const { intent, stored, token } = command;
  const next = draftOf(input);
  const registered = token !== null;
  let keep: IConfigSettleSession = {};
  if (registered) {
    const account: IEntityClientAccount = {
      kind: 'registered', playerId: token.playerId, displayName: null, email: null, matchCredits: session.account.matchCredits,
    };
    next.session.account = account;
    if (stored.guest !== null) keep = { guest: null };
  } else {
    const account: IEntityClientAccount = stored.guest !== null
      ? { kind: 'guest', playerId: stored.guest.playerId, displayName: stored.guest.displayName, email: null, matchCredits: session.account.matchCredits }
      : { kind: 'anonymous', playerId: null, displayName: null, email: null, matchCredits: session.account.matchCredits };
    next.session.account = account;
  }
  const hasIdentity = registered || stored.guest !== null;
  const joinCode = (intent.joinCode ?? '').trim().toUpperCase();
  next.session.restoring = stored.resume !== null;
  if (intent.paymentReturn) {
    next.session.screen = 'PAYMENT_SUCCESS';
  } else if (joinCode !== '') {
    next.session.pendingRoomCode = joinCode;
    next.session.pendingJoinError = null;
    next.session.screen = 'GAME';
  } else {
    next.session.screen = hasIdentity && stored.resume !== null ? 'GAME' : 'WELCOME';
  }
  return decided(settleSession(input, next, keep), APPLIED);
}

/**
 * The game screen mounted. Refused while an entry is in flight — StrictMode mounts twice — or when the
 * tab already sits at a table. Identity: a known player, a stored guest seated now (free tier, no
 * credits), the server's `me` for a token, or a name to ask for. The stored game: nothing (the overlay
 * drops), another player's record, trusted as it is without asking the server (it belongs to an identity
 * that has not hydrated yet — clearing it would lose the game), or this player's, checked by its code.
 */
export function decidePlanEntry(input: IPropsClientInput, command: ICommandPlanEntry): TSession<IEntityEntryPlan> {
  const { table, session } = input;
  if (session.entering) return refuse('The game screen is already being entered.', REFUSAL.ALREADY);
  if (table.room !== null) return refuse('Already at a table.', REFUSAL.ALREADY);
  const next = draftOf(input);
  next.session.entering = true;

  let identity: IEntityEntryPlan['identity'] = 'name';
  if (session.player !== null) identity = 'known';
  else if (session.account.kind === 'registered') identity = 'me';
  else {
    const guest = session.account.kind === 'guest' ? guestPlayer(session) : null;
    if (guest !== null) {
      withPlayer(next, guest);
      identity = 'known';
    }
  }

  const record = command.stored.resume;
  let resume: IEntityEntryPlan['resume'] = 'validate';
  if (record === null) {
    resume = 'none';
    next.session.restoring = false;
  } else if (record.playerId !== session.account.playerId) {
    resume = 'trust';
  }
  const dto: IEntityEntryPlan = { identity, resume, record };
  return decided(settleSession(input, next), dto);
}

/**
 * `me` answered. A player behind the token is seated, with their credits; one already seated is left
 * alone (the answer is late). No player: the token is worthless and the tab signs out — after the
 * expiry message, if a join was waiting on it, so the sign-out's reset leaves it showing.
 */
export function decideMeAnswered(input: IPropsClientInput, command: ICommandMeAnswered): TSession<IEntitySignOut> {
  const { session } = input;
  const { me } = command;
  const next = draftOf(input);
  if (me === null) {
    if (session.pendingRoomCode) next.session.pendingJoinError = SESSION_EXPIRED_MESSAGE;
    const signOut: IEntitySignOut = { signOut: true };
    return decided(settleSession(input, next), signOut);
  }
  const stay: IEntitySignOut = { signOut: false };
  if (session.player !== null) return decided(NOTHING, stay);
  const player: IEntityClientPlayer = { playerId: me.id, displayName: me.displayName, matchCredits: me.matchCredits, isAdmin: me.isAdmin };
  withPlayer(next, player);
  next.session.account = { ...session.account, matchCredits: me.matchCredits };
  return decided(settleSession(input, next), stay);
}

/**
 * `me` could not be asked — the server unreachable, or it failed (FE3; until then this read as "no player"
 * and signed the player out). The account stays signed in; the entry stops, and the welcome screen says
 * why, so Play enters again and asks again. Unless the stored game seated the tab meanwhile: then it plays.
 */
export function decideMeUnreachable(input: IPropsClientInput, _command: ICommandNothing): TSession<IEntityTableAck> {
  if (input.table.room !== null) return decided(NOTHING, NOT_APPLIED);
  const next = draftOf(input);
  next.session.screen = 'WELCOME';
  next.session.pendingJoinError = ME_UNREACHABLE;
  return decided(settleSession(input, next), APPLIED);
}

/** The stored game's room, by its code: gone, unreachable or finished — forget it; otherwise go on. */
export function decideResumeValidated(input: IPropsClientInput, command: ICommandResumeValidated): TSession<IEntityProceed> {
  const { lookup } = command;
  if (lookup !== null && lookup.roomStatus !== ROOM_FINISHED) return decided(NOTHING, GO_ON);
  const next = draftOf(input);
  next.session.restoring = false;
  const forget: IConfigSettleSession = { resume: null };
  return decided(settleSession(input, next, forget), STAY);
}

/** Back into a stored game (`resumeIntoGame`): the overlay goes up, the record is kept, the room is re-joined. */
export function decidePlanRestore(input: IPropsClientInput, command: ICommandPlanRestore): TSession<IPayloadSeat> {
  const { record } = command;
  const next = draftOf(input);
  next.session.restoring = true;
  const keep: IConfigSettleSession = { resume: record };
  const request: IPayloadSeat = { roomId: record.roomId, playerId: record.playerId };
  return decided(settleSession(input, next, keep), request);
}

/**
 * The stored game's room answered the join. None: the game is over for this player — forget it. A
 * table that has not started: the lobby. One in progress: read its round next, still restoring. The
 * seat is the record's player, whoever the session has caught up to.
 */
export function decideRestoreJoined(input: IPropsClientInput, command: ICommandRestoreJoined): TSession<IEntityRoundRead> {
  const { record, room } = command;
  const next = draftOf(input);
  if (room === null) {
    next.session.restoring = false;
    const forget: IConfigSettleSession = { resume: null };
    return decided(settleSession(input, next, forget), NO_READ);
  }
  seated(next, restoredRoom(room, record));
  if (room.status !== ROOM_IN_PROGRESS) {
    inLobby(next);
    next.session.restoring = false;
    return decided(settleSession(input, next), NO_READ);
  }
  const read: IEntityRoundRead = { read: { roomId: room.id, playerId: record.playerId } };
  return decided(settleSession(input, next), read);
}

/**
 * The live round, after a seat was taken mid-game — a restore, a rejoin, a late join. FE1's mapping
 * (`applyActiveRound`), as the request's player: the restore runs before `me` may have answered.
 * Ignored when the tab no longer sits at that room.
 */
export function decideRoundRestored(input: IPropsClientInput, command: ICommandRoundRestored): TSession<IEntityTableAck> {
  const { table } = input;
  const { request, activeRound } = command;
  if (table.room === null || table.room.roomId !== request.roomId) return decided(NOTHING, NOT_APPLIED);
  const next = draftOf(input);
  applyActiveRound(next.table, activeRound, request.playerId, table.room.players.map((p) => p.id));
  next.session.restoring = false;
  next.session.pendingRoomCode = null;
  return decided(settleSession(input, next), APPLIED);
}

/**
 * Identity and the stored game have settled. Seated: nothing to do. No player yet: wait for the name.
 * Then, as the hooks' guards had it: a pending code joins (and rules out the cold rejoin); a registered
 * player with nothing stored asks the server for a live room; the funnel's host intent creates a table;
 * and with nothing left, MainMenuPanel's redirect — back to the welcome screen.
 */
export function decidePlanIntent(input: IPropsClientInput, command: ICommandPlanIntent): TSession<IEntityIntent> {
  const { table, session } = input;
  const next = draftOf(input);
  let step: IEntityIntent['next'] = 'idle';
  if (table.room !== null) step = 'seated';
  else if (session.player === null) step = 'wait';
  else if (session.pendingRoomCode) step = 'join';
  else if (!command.coldChecked && session.account.kind === 'registered') step = 'cold';
  else if (session.hostOnEntry) step = 'host';
  else next.session.screen = 'WELCOME';
  const dto: IEntityIntent = { next: step };
  return decided(settleSession(input, next), dto);
}

/** The entry is over, however it ended; the next mount may enter again. */
export function decideEntryFinished(input: IPropsClientInput, _command: ICommandNothing): TSession<IEntityTableAck> {
  const next = draftOf(input);
  next.session.entering = false;
  return decided(settleSession(input, next), APPLIED);
}
